import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { SkillMeta } from '../../types/skill';

interface SkillCardProps {
  skill: SkillMeta;
  onClick?: () => void;
  onAttach?: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  process: 'bg-blue-100 text-blue-700',
  design: 'bg-pink-100 text-pink-700',
  review: 'bg-purple-100 text-purple-700',
  generation: 'bg-green-100 text-green-700',
  debugging: 'bg-red-100 text-red-700',
  ops: 'bg-orange-100 text-orange-700',
};

const CATEGORY_COLORS_DARK: Record<string, string> = {
  process: 'bg-blue-900/40 text-blue-300',
  design: 'bg-pink-900/40 text-pink-300',
  review: 'bg-purple-900/40 text-purple-300',
  generation: 'bg-green-900/40 text-green-300',
  debugging: 'bg-red-900/40 text-red-300',
  ops: 'bg-orange-900/40 text-orange-300',
};

export const SkillCard: React.FC<SkillCardProps> = ({ skill, onClick, onAttach }) => {
  const { darkMode } = useTheme();

  const categoryColor = skill.categories[0]
    ? (darkMode ? CATEGORY_COLORS_DARK[skill.categories[0]] : CATEGORY_COLORS[skill.categories[0]])
    : (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600');

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onClick) { e.preventDefault(); onClick(); } }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={`rounded-lg border p-4 flex flex-col gap-2 transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        onClick ? 'cursor-pointer' : ''
      } ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
    >
      {/* Name + category */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold text-sm truncate">{skill.display_name}</h3>
        {skill.categories[0] && (
          <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${categoryColor}`}>
            {skill.categories[0]}
          </span>
        )}
      </div>

      {/* Description — 2-line clamp */}
      <p className={`text-xs line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {skill.description}
      </p>

      {/* Footer: source + tokens + code badge + attach */}
      <div className="flex items-center justify-between mt-auto pt-1">
        <div className="flex items-center gap-2 flex-wrap">
          {skill.source_id && (
            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {skill.source_id}
            </span>
          )}
          {skill.estimated_tokens > 0 && (
            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              ~{skill.estimated_tokens.toLocaleString()} tokens
            </span>
          )}
          {skill.has_code_blocks && (
            <span
              title="Contains executable code blocks — not supported for injection"
              className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                darkMode
                  ? 'bg-amber-900/40 text-amber-400 border border-amber-700'
                  : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}
            >
              ⚙ code
            </span>
          )}
        </div>
        {onAttach && (
          <button
            onClick={(e) => { e.stopPropagation(); onAttach(); }}
            className={`text-xs px-2 py-0.5 rounded-md font-medium transition-colors ${
              darkMode
                ? 'bg-blue-700 hover:bg-blue-600 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            Attach
          </button>
        )}
      </div>
    </div>
  );
};
